/**
 * Cart Store - Shopify-freie Implementierung
 * Lokales Cart-Management mit Zustand + localStorage Persistenz
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { trackFunnelEvent } from '@/hooks/useFunnelTracking';

// ============================================================
// Types
// ============================================================

export interface CardCustomization {
  formData: Record<string, string>;
  photo: string | null; // Base64 or URL after upload
  signatureText: string;
  signatureFont: string;
  templateId?: string;
  templateSlug?: string;
  templateName?: string;
  cardPreviewDataUrl?: string; // Generated card thumbnail for cart display
  cartDisplayName?: string; // Custom cart display name from template settings
}

export interface CartItemPrice {
  amount: string;
  currencyCode: string;
}

export interface CartItem {
  localId: string;
  
  // Product Info (from card_templates or products table)
  productId: string; // template UUID or product UUID
  productType: 'template' | 'product';
  productName: string;
  productSlug: string;
  productImage?: string;
  
  // Variant/Option (e.g., "Standard", "Express")
  variantId?: string;
  variantTitle?: string;
  
  // Pricing
  price: CartItemPrice;
  compareAtPrice?: CartItemPrice;
  
  // Quantity
  quantity: number;
  
  // Card Customization (only for templates)
  customization?: CardCustomization;
}

export interface AppliedDiscount {
  code: string;
  type: 'percentage' | 'fixed_amount';
  value: number;
  amount: number; // legacy/initial – use discountAmount() for current value
}

interface CartStore {
  items: CartItem[];
  isOpen: boolean;
  isLoading: boolean;
  lastAddedItemId: string | null;
  appliedDiscount: AppliedDiscount | null;
  
  // Actions
  addItem: (item: Omit<CartItem, 'localId'>) => void;
  updateQuantity: (localId: string, quantity: number) => void;
  updateCustomization: (localId: string, customization: CardCustomization) => void;
  removeItem: (localId: string) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  clearLastAddedItemId: () => void;
  applyDiscount: (discount: AppliedDiscount) => void;
  removeDiscount: () => void;
  
  // Computed
  cartCount: () => number;
  cartSubtotal: () => number;
  discountAmount: () => number;
  cartTotal: () => number;
}

// Generate unique local IDs for cart items
const generateLocalId = () => `cart-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      isLoading: false,
      lastAddedItemId: null,
      appliedDiscount: null,

      addItem: (itemData) => {
        const { items } = get();
        const localId = generateLocalId();
        const item: CartItem = { ...itemData, localId };
        
        // For customized items (cards), always add as separate line
        const isCustomized = !!itemData.customization;
        
        // For non-customized products, check if same product/variant exists
        const existingItem = !isCustomized 
          ? items.find(i => 
              i.productId === itemData.productId && 
              i.variantId === itemData.variantId && 
              !i.customization
            )
          : null;

        if (existingItem) {
          // Increment quantity of existing item
          set({
            items: items.map(i => 
              i.localId === existingItem.localId 
                ? { ...i, quantity: i.quantity + item.quantity }
                : i
            ),
            isOpen: true,
            lastAddedItemId: existingItem.localId,
          });
        } else {
          // Add as new item
          set({
            items: [...items, item],
            isOpen: true,
            lastAddedItemId: item.localId,
          });
        }
        
        console.log('[Cart] Item added:', {
          localId: item.localId,
          productName: item.productName,
          isCustomized,
          totalItems: get().items.length,
        });
      },

      updateQuantity: (localId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(localId);
          return;
        }
        
        const { items } = get();
        set({
          items: items.map(i => 
            i.localId === localId ? { ...i, quantity } : i
          ),
        });
      },

      updateCustomization: (localId, customization) => {
        const { items } = get();
        set({
          items: items.map(i =>
            i.localId === localId ? { ...i, customization } : i
          ),
        });
      },

      removeItem: (localId) => {
        const { items } = get();
        const item = items.find(i => i.localId === localId);
        
        if (!item) {
          console.warn('[Cart] removeItem: Item not found with localId:', localId);
          return;
        }

        const newItems = items.filter(i => i.localId !== localId);
        set({ items: newItems });
        
        // Track cart removal
        trackFunnelEvent('cart_removed', {
          product_slug: item.productSlug,
          product_name: item.productName,
          cart_value: parseFloat(item.price.amount),
          items_count: newItems.length,
        });
        
        console.log('[Cart] Item removed:', {
          localId,
          productName: item.productName,
          remainingItems: newItems.length,
        });
      },

      clearCart: () => {
        console.log('[Cart] Cart cleared');
        set({ items: [], appliedDiscount: null });
      },

      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),
      clearLastAddedItemId: () => set({ lastAddedItemId: null }),
      
      applyDiscount: (discount) => {
        set({ appliedDiscount: discount });
        console.log('[Cart] Discount applied:', discount.code, discount.amount);
      },
      
      removeDiscount: () => {
        set({ appliedDiscount: null });
        console.log('[Cart] Discount removed');
      },
      
      cartCount: () => get().items.reduce((sum, item) => sum + item.quantity, 0),
      
      cartSubtotal: () => get().items.reduce(
        (sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 
        0
      ),

      discountAmount: () => {
        const discount = get().appliedDiscount;
        if (!discount) return 0;
        const subtotal = get().items.reduce(
          (sum, item) => sum + (parseFloat(item.price.amount) * item.quantity),
          0
        );
        if (discount.type === 'percentage') {
          return Math.min(Math.round((subtotal * discount.value / 100) * 100) / 100, subtotal);
        }
        return Math.min(discount.value, subtotal);
      },

      cartTotal: () => {
        const subtotal = get().items.reduce(
          (sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 
          0
        );
        const discountAmt = get().discountAmount();
        return Math.max(0, subtotal - discountAmt);
      },
    }),
    {
      name: 'shop-cart', // Storage key for localStorage
      storage: createJSONStorage(() => localStorage),
      // Only persist items, not UI state
      partialize: (state) => ({
        items: state.items.map(item => ({
          ...item,
          customization: item.customization 
            ? { 
                ...item.customization, 
                // Exclude large generated thumbnail from persistence
                cardPreviewDataUrl: undefined,
              }
            : undefined,
        })),
        appliedDiscount: state.appliedDiscount,
      }),
      // Merge persisted state with initial state on hydration
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<CartStore> | undefined;
        return {
          ...currentState,
          items: persisted?.items || [],
          appliedDiscount: persisted?.appliedDiscount || null,
        };
      },
      // Ensure persistence works correctly across auth state changes
      onRehydrateStorage: () => (state) => {
        if (state) {
          console.log('[Cart] Rehydrated from storage:', state.items.length, 'items');
        }
        useCartHydration.setState({ hasHydrated: true });
      },
    }
  )
);

// ============================================================
// Hydration state (separate store to avoid circular deps)
// ============================================================
export const useCartHydration = create<{ hasHydrated: boolean }>(() => ({
  hasHydrated: false,
}));

// Safety: if persist middleware never fires onRehydrateStorage (e.g. storage
// unavailable), force hydration after a short timeout so the UI isn't stuck.
if (typeof window !== 'undefined') {
  setTimeout(() => {
    if (!useCartHydration.getState().hasHydrated) {
      console.warn('[Cart] Hydration timeout – forcing hasHydrated');
      useCartHydration.setState({ hasHydrated: true });
    }
  }, 2000);
}

// ============================================================
// Helper: Create CartItem from Template
// ============================================================
export function createCartItemFromTemplate(
  template: {
    id: string;
    name: string;
    slug: string;
    price: number;
    compare_at_price?: number | null;
    thumbnail_front_url?: string | null;
  },
  customization: CardCustomization,
  quantity: number = 1
): Omit<CartItem, 'localId'> {
  return {
    productId: template.id,
    productType: 'template',
    productName: template.name,
    productSlug: template.slug,
    productImage: template.thumbnail_front_url || undefined,
    price: {
      amount: template.price.toFixed(2),
      currencyCode: 'EUR',
    },
    compareAtPrice: template.compare_at_price ? {
      amount: template.compare_at_price.toFixed(2),
      currencyCode: 'EUR',
    } : undefined,
    quantity,
    customization,
  };
}

// ============================================================
// Helper: Create CartItem from Product
// ============================================================
export function createCartItemFromProduct(
  product: {
    id: string;
    name: string;
    slug: string;
    price: number;
    compare_at_price?: number | null;
    images?: Array<{ url: string }> | null;
  },
  quantity: number = 1,
  variantId?: string,
  variantTitle?: string
): Omit<CartItem, 'localId'> {
  const firstImage = Array.isArray(product.images) && product.images.length > 0 
    ? product.images[0].url 
    : undefined;
    
  return {
    productId: product.id,
    productType: 'product',
    productName: product.name,
    productSlug: product.slug,
    productImage: firstImage,
    variantId,
    variantTitle,
    price: {
      amount: product.price.toFixed(2),
      currencyCode: 'EUR',
    },
    compareAtPrice: product.compare_at_price ? {
      amount: product.compare_at_price.toFixed(2),
      currencyCode: 'EUR',
    } : undefined,
    quantity,
  };
}
